import { Text, tokens } from "@ui"
import { css, keyframes } from "dx-styles"
import type { ReactElement } from "react"

const auraSpin = keyframes({
  from: { transform: "rotate(0deg)" },
  to: { transform: "rotate(360deg)" },
})

const auraBreathe = keyframes({
  "0%, 100%": { transform: "scale(0.92)", opacity: 0.65 },
  "50%": { transform: "scale(1.06)", opacity: 1 },
})

const bob = keyframes({
  "0%, 100%": { transform: "translateY(4px) rotate(-1.6deg)" },
  "50%": { transform: "translateY(-6px) rotate(1.6deg)" },
})

const blink = keyframes({
  "0%, 84%, 100%": { transform: "scaleY(1)" },
  "88%": { transform: "scaleY(0.06)" },
  "92%": { transform: "scaleY(1)" },
})

const flutterStart = keyframes({
  "0%, 100%": { transform: "rotate(0deg)" },
  "50%": { transform: "rotate(-8deg)" },
})

const flutterEnd = keyframes({
  "0%, 100%": { transform: "rotate(0deg)" },
  "50%": { transform: "rotate(8deg)" },
})

const orbit = keyframes({
  from: { transform: "rotate(0deg)" },
  to: { transform: "rotate(360deg)" },
})

const twinkle = keyframes({
  "0%, 100%": { opacity: 0.25 },
  "50%": { opacity: 1 },
})

const hop = keyframes({
  "0%, 80%, 100%": { opacity: 0.25, transform: "translateY(0)" },
  "40%": { opacity: 1, transform: "translateY(-4px)" },
})

const wrapper = css({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: tokens.space.sm,
  width: "100%",
  minHeight: "60vh",
  paddingBlock: tokens.space.lg,
  overflow: "hidden",
})

const auraOrbit = css({
  position: "absolute",
  insetBlockStart: "50%",
  insetInlineStart: "50%",
  width: "520px",
  height: "520px",
  marginBlockStart: "-290px",
  marginInlineStart: "-260px",
  pointerEvents: "none",
  animation: `${auraSpin} 32s linear infinite`,
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
})

const aura = css({
  width: "100%",
  height: "100%",
  filter: "blur(4px)",
  backgroundImage: [
    "radial-gradient(44% 44% at 32% 64%, rgba(250, 176, 72, 0.9) 0%, rgba(250, 176, 72, 0.45) 34%, rgba(250, 176, 72, 0) 70%)",
    "radial-gradient(36% 36% at 60% 74%, rgba(240, 150, 162, 0.5) 0%, rgba(240, 150, 162, 0) 72%)",
    "radial-gradient(46% 46% at 70% 38%, rgba(199, 150, 245, 0.85) 0%, rgba(199, 150, 245, 0.4) 40%, rgba(199, 150, 245, 0) 74%)",
    "radial-gradient(40% 40% at 34% 32%, rgba(214, 180, 250, 0.6) 0%, rgba(214, 180, 250, 0) 76%)",
  ].join(", "),
  animation: `${auraBreathe} 7s ease-in-out infinite`,
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
})

const stage = css({
  position: "relative",
  width: "220px",
  height: "220px",
})

const owl = css({
  position: "relative",
  display: "block",
  width: "100%",
  height: "100%",
  overflow: "visible",
})

const owlFloat = css({
  transformBox: "fill-box",
  transformOrigin: "center",
  animation: `${bob} 3.6s ease-in-out infinite`,
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
})

const eye = css({
  transformBox: "fill-box",
  transformOrigin: "center",
  animation: `${blink} 5.2s ease-in-out infinite`,
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
})

const eyeEnd = css({ animationDelay: "80ms" })

const wingStart = css({
  transformBox: "fill-box",
  transformOrigin: "top center",
  animation: `${flutterStart} 3.6s ease-in-out infinite`,
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
})

const wingEnd = css({
  transformBox: "fill-box",
  transformOrigin: "top center",
  animation: `${flutterEnd} 3.6s ease-in-out infinite`,
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
})

const motes = css({
  transformOrigin: "110px 110px",
  animation: `${orbit} 16s linear infinite`,
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
})

const mote = css({
  animation: `${twinkle} 2.8s ease-in-out infinite`,
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
    opacity: 0.6,
  },
})

const moteDelayMid = css({ animationDelay: "0.9s" })
const moteDelayLate = css({ animationDelay: "1.8s" })

const copy = css({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: tokens.space.xs,
  textAlign: "center",
})

const dots = css({
  display: "flex",
  gap: tokens.space.xs,
  marginBlockStart: tokens.space.xs,
})

const dot = css({
  width: "6px",
  height: "6px",
  borderRadius: tokens.radius.full,
  backgroundColor: tokens.color.primary,
  animation: `${hop} 1.4s ease-in-out infinite`,
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
    opacity: 0.6,
  },
})

const dotDelayMid = css({ animationDelay: "0.18s" })
const dotDelayLate = css({ animationDelay: "0.36s" })

export const Loading = (): ReactElement => {
  return (
    <div
      className={wrapper}
      role="status"
      aria-live="polite"
    >
      <div className={auraOrbit}>
        <div className={aura} />
      </div>

      <div className={stage}>
        <svg
          viewBox="0 0 220 220"
          className={owl}
          aria-hidden="true"
        >
          <defs>
            <linearGradient
              id="kbOwlBody"
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop
                offset="0%"
                stopColor="#9B6BBE"
              />
              <stop
                offset="55%"
                stopColor="#7E4797"
              />
              <stop
                offset="100%"
                stopColor="#462853"
              />
            </linearGradient>
            <linearGradient
              id="kbOwlDisc"
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop
                offset="0%"
                stopColor="#FFF6FF"
              />
              <stop
                offset="100%"
                stopColor="#F1DEFB"
              />
            </linearGradient>
            <linearGradient
              id="kbOwlBeak"
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop
                offset="0%"
                stopColor="#FFB84D"
              />
              <stop
                offset="100%"
                stopColor="#FF9903"
              />
            </linearGradient>
          </defs>

          <g className={motes}>
            <circle
              className={mote}
              cx="110"
              cy="12"
              r="5"
              fill="#FF9903"
            />
            <circle
              className={`${mote} ${moteDelayMid}`}
              cx="194.9"
              cy="159"
              r="4"
              fill="#FF375B"
            />
            <circle
              className={`${mote} ${moteDelayLate}`}
              cx="25.1"
              cy="159"
              r="6"
              fill="#33C4B3"
            />
          </g>

          <g className={owlFloat}>
            <ellipse
              cx="110"
              cy="188"
              rx="46"
              ry="8"
              fill="rgba(70, 40, 83, 0.14)"
            />

            <path
              d="M74 60 L68 28 L94 46 Z"
              fill="#7E4797"
            />
            <path
              d="M146 60 L152 28 L126 46 Z"
              fill="#7E4797"
            />

            <path
              d="M110 34 C148 34 168 62 168 100 C168 140 144 172 110 172 C76 172 52 140 52 100 C52 62 72 34 110 34 Z"
              fill="url(#kbOwlBody)"
            />

            <ellipse
              cx="110"
              cy="132"
              rx="30"
              ry="30"
              fill="rgba(255, 255, 255, 0.16)"
            />

            <path
              className={wingStart}
              d="M62 88 C48 106 50 140 68 156 C75 161 81 157 79 149 C71 130 68 108 74 90 C76 81 67 80 62 88 Z"
              fill="rgba(255, 255, 255, 0.3)"
            />
            <path
              className={wingEnd}
              d="M158 88 C172 106 170 140 152 156 C145 161 139 157 141 149 C149 130 152 108 146 90 C144 81 153 80 158 88 Z"
              fill="rgba(255, 255, 255, 0.3)"
            />

            <circle
              cx="90"
              cy="86"
              r="25"
              fill="url(#kbOwlDisc)"
            />
            <circle
              cx="130"
              cy="86"
              r="25"
              fill="url(#kbOwlDisc)"
            />

            <g className={eye}>
              <circle
                cx="90"
                cy="86"
                r="13"
                fill="#FFFFFF"
              />
              <circle
                cx="92"
                cy="87"
                r="7"
                fill="#0C1436"
              />
              <circle
                cx="89"
                cy="84"
                r="2.4"
                fill="#FFFFFF"
              />
            </g>
            <g className={`${eye} ${eyeEnd}`}>
              <circle
                cx="130"
                cy="86"
                r="13"
                fill="#FFFFFF"
              />
              <circle
                cx="128"
                cy="87"
                r="7"
                fill="#0C1436"
              />
              <circle
                cx="125"
                cy="84"
                r="2.4"
                fill="#FFFFFF"
              />
            </g>

            <path
              d="M110 92 L119 104 L110 118 L101 104 Z"
              fill="url(#kbOwlBeak)"
            />

            <path
              d="M94 168 L94 180 M94 180 L86 186 M94 180 L102 186"
              stroke="#FF9903"
              strokeWidth="5"
              strokeLinecap="round"
              fill="none"
            />
            <path
              d="M126 168 L126 180 M126 180 L118 186 M126 180 L134 186"
              stroke="#FF9903"
              strokeWidth="5"
              strokeLinecap="round"
              fill="none"
            />
          </g>
        </svg>
      </div>

      <div className={copy}>
        <Text variant="h3">Waking the knowledge owl</Text>
        <Text variant="description">
          Gathering articles from your knowledge base
        </Text>
        <div className={dots}>
          <span className={dot} />
          <span className={`${dot} ${dotDelayMid}`} />
          <span className={`${dot} ${dotDelayLate}`} />
        </div>
      </div>
    </div>
  )
}
